import React from 'react';
import PropTypes from 'prop-types';

import { StudyItem } from '../StudyItem';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { ScrollArea } from '../ScrollArea';

const noop = () => {};

const StudyBrowser = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  servicesManager,
  showSettings,
  viewPresets,
  onLoadStudy,
  loadedStudyUIDs,
  ThumbnailMenuItems,
  StudyMenuItems,
}: withAppTypes) => {
  const getTabContent = () => {
    const tabData = tabs.find(tab => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? viewPresets.filter(preset => preset.selected)[0]?.id
      : 'thumbnails';
    return tabData?.studies?.map(
      ({ studyInstanceUid, date, description, numInstances, modalities, displaySets, patientName }) => {
        const isExpanded = expandedStudyInstanceUIDs.includes(studyInstanceUid);

        return (
          <React.Fragment key={studyInstanceUid}>
            <StudyItem
              date={date}
              description={description}
              patientName={patientName}
              numInstances={numInstances}
              isExpanded={isExpanded}
              displaySets={displaySets}
              modalities={modalities}
              isActive={isExpanded}
              onClick={() => onClickStudy(studyInstanceUid)}
              onClickThumbnail={onClickThumbnail}
              onDoubleClickThumbnail={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              data-cy="thumbnail-list"
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              StudyMenuItems={StudyMenuItems}
              StudyInstanceUID={studyInstanceUid}
              isStudyLoaded={activeTabName !== 'primary' && loadedStudyUIDs?.includes(studyInstanceUid)}
              onLoadStudy={activeTabName !== 'primary' && onLoadStudy && !loadedStudyUIDs?.includes(studyInstanceUid) ? onLoadStudy : undefined}
            />
          </React.Fragment>
        );
      }
    );
  };

  // Only show View selector when tabs actually have different studies
  const nonEmptyTabs = tabs ? tabs.filter(tab => tab.studies && tab.studies.length > 0) : [];
  let showViewSelector = false;
  if (nonEmptyTabs.length > 1) {
    const firstUids = new Set(nonEmptyTabs[0].studies.map(s => s.studyInstanceUid));
    showViewSelector = nonEmptyTabs.some(tab => {
      const tabUids = new Set(tab.studies.map(s => s.studyInstanceUid));
      if (tabUids.size !== firstUids.size) return true;
      return [...tabUids].some(uid => !firstUids.has(uid));
    });
  }

  return (
    <ScrollArea>
      <div
        className="bg-background flex flex-1 flex-col gap-[4px]"
        data-cy={'studyBrowser-panel'}
      >
        <div className="flex flex-col gap-[4px]">
          {showSettings && (
            <div className="mx-2 mt-1.5 mb-0.5 rounded-xl bg-card p-3 shadow-[0_0_0_1px_hsl(var(--primary)/0.12),0_4px_20px_rgba(0,0,0,0.4)]">
              <div className="flex flex-col gap-2">
                {showViewSelector && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="w-7 shrink-0 text-[9px] font-semibold uppercase tracking-widest text-white/25">View</span>
                      <StudyBrowserViewOptions
                        tabs={tabs}
                        onSelectTab={onClickTab}
                        activeTabName={activeTabName}
                      />
                    </div>
                    <div className="h-px bg-white/[0.05]" />
                  </>
                )}
                <div className="flex items-center gap-2">
                  <span className="w-7 shrink-0 text-[9px] font-semibold uppercase tracking-widest text-white/25">Sort</span>
                  <StudyBrowserSort servicesManager={servicesManager} />
                </div>
              </div>
            </div>
          )}
          {getTabContent()}
        </div>
      </div>
    </ScrollArea>
  );
};

StudyBrowser.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: PropTypes.any,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              /**
               * Data the thumbnail should expose to a receiving drop target. Use a matching
               * `dragData.type` to identify which targets can receive this draggable item.
               * If this is not set, drag-n-drop will be disabled for this thumbnail.
               *
               * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
               */
              dragData: PropTypes.shape({
                /** Must match the "type" a dropTarget expects */
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ).isRequired,
    })
  ),
  StudyMenuItems: PropTypes.func,
};

export { StudyBrowser };
